import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { toast, Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicConsent() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);
  const sigRef = useRef();

  useEffect(() => {
    axios.get(`${API}/public/consent/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || t("publicConsent.invalidLink")));
  }, [token, t]);

  async function sign() {
    if (sigRef.current.isEmpty()) { toast.error(t("publicConsent.signBeforeContinue")); return; }
    setSigning(true);
    try {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await axios.post(`${API}/public/consent/${token}/sign`, { signature_data_url: dataUrl });
      setDone(true); toast.success(t("publicConsent.thanks"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setSigning(false); }
  }

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6 text-center relative">
    <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
    <div><p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("common.error")}</p>
    <h2 className="font-heading text-3xl font-light">{err}</h2></div>
  </div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-stone-500 relative">
    <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
    {t("common.loading")}
  </div>;

  if (done || data.status === "signed") return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6 text-center relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="max-w-md fade-in-up">
        <div className="w-16 h-16 rounded-full bg-[#8FA08B] mx-auto mb-6 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M5 12l5 5L20 7"/></svg>
        </div>
        <h2 className="font-heading text-4xl font-light mb-3">{t("publicConsent.signed")}</h2>
        <p className="text-stone-600">{t("publicConsent.thanks")}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-10 px-4">
      <Toaster position="top-center" richColors />
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="max-w-2xl mx-auto fade-in-up">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">{t("publicConsent.eyebrow")}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-light">{data.treatment}</h1>
          <p className="text-stone-500 mt-3">{t("publicConsent.patient")} <b>{data.patient_name}</b></p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-6">
          <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{data.body_text}</div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("publicConsent.signHere")}</label>
            <div className="border border-stone-200 rounded-2xl bg-[#F5F5F4] overflow-hidden touch-none">
              <SignatureCanvas ref={sigRef} canvasProps={{ className: "w-full h-56", "data-testid": "public-signature" }} />
            </div>
            <div className="flex gap-3 mt-3">
              <button onClick={() => sigRef.current.clear()} data-testid="public-clear"
                className="px-5 py-2 rounded-full border border-stone-200 text-sm hover:bg-stone-50">{t("patientDetail.clear")}</button>
              <button onClick={sign} disabled={signing} data-testid="public-sign"
                className="flex-1 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
                {signing ? t("publicConsent.signing") : t("publicConsent.signAndAccept")}
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-stone-400 text-center mt-6">{t("publicConsent.footer")}</p>
      </div>
    </div>
  );
}
